import React from 'react';
import { User, Mail, Phone, Shield, BadgeCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';

const ROLE_LABEL_KEYS: Record<string, string> = {
  team_sales: 'teams.roles.accountExecutive',
  team_customer_success: 'teams.roles.customerSuccess',
  team_account_manager: 'teams.roles.accountManager',
  team_manager: 'teams.roles.manager',
  team_marketing_manager: 'teams.roles.marketingManager',
};

const ROLE_COLORS: Record<string, string> = {
  team_sales: 'bg-blue-100 text-blue-800',
  team_customer_success: 'bg-green-100 text-green-800',
  team_account_manager: 'bg-indigo-100 text-indigo-800',
  team_manager: 'bg-purple-100 text-purple-800',
  team_marketing_manager: 'bg-amber-100 text-amber-800',
};

const TeamProfileTab: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const member = user?.teamMember;
  const roleKey = user?.role ?? '';
  const labelKey = ROLE_LABEL_KEYS[roleKey];
  const roleLabel = labelKey ? t(labelKey) : (member?.role ?? roleKey);
  const roleColor = ROLE_COLORS[roleKey] ?? 'bg-gray-100 text-gray-800';

  const firstName = member?.firstName ?? user?.name?.split(' ')[0] ?? '';
  const lastName = member?.lastName ?? user?.name?.split(' ').slice(1).join(' ') ?? '';
  const email = member?.email ?? user?.email ?? '';
  const phone = member?.phone ?? '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  return (
    <div>
      <div className="h-24 bg-gradient-to-r from-[#4339A0] to-[#6C63FF] -mx-6 -mt-6 mb-6 rounded-t-xl" />

      <div className="-mt-16 mb-6 flex items-end justify-between">
        <div className="w-24 h-24 rounded-full bg-[#4339A0] border-4 border-white flex items-center justify-center shadow-lg">
          {member?.avatar ? (
            <img
              src={member.avatar}
              alt={`${firstName} ${lastName}`}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className="text-2xl font-bold text-white">{initials || <User className="w-8 h-8" />}</span>
          )}
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${roleColor}`}>
          {roleLabel}
        </span>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        {firstName} {lastName}
      </h2>

      <div className="space-y-4">
        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
          <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">{t('teams.profile.email')}</p>
            <p className="text-gray-900 font-medium truncate">{email}</p>
          </div>
          <span className="ml-auto text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded">{t('teams.profile.readOnly')}</span>
        </div>

        {phone ? (
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
            <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">{t('teams.profile.phone')}</p>
              <p className="text-gray-900 font-medium">{phone}</p>
            </div>
          </div>
        ) : null}

        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
          <Shield className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">{t('teams.profile.role')}</p>
            <p className="text-gray-900 font-medium">{roleLabel}</p>
          </div>
        </div>

        {member?.status && (
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
            <BadgeCheck className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">{t('teams.profile.status')}</p>
              <p className="text-gray-900 font-medium capitalize">{member.status.toLowerCase()}</p>
            </div>
          </div>
        )}
      </div>

      <p className="mt-6 text-sm text-gray-500">
        {t('teams.profile.contactAdmin')}
      </p>
    </div>
  );
};

export default TeamProfileTab;
